import { type FC, type ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from "react"
import styled, { css, keyframes } from "styled-components"

import { lockBodyScroll } from "../utilities/modalScrollLock"
import Form from "./Form"
import Portal from "./Portal"

type State = "idle" | "open" | "closed"

type Props = {
	isOpen: boolean
	/**
	 * Paints the modal above floating UI that carries its own z-index, for modals opened over such content.
	 * Off by default because tooltips and toasts sit low on purpose and should stay above an ordinary modal.
	 */
	isElevated?: boolean
	width?: string | number
	onSubmit?(): void
	/** Called when the user presses Escape. Without it, Escape leaves the modal open. */
	onClose?(): void
	/** Accessible name for modals without an h1–h3 title. */
	ariaLabel?: string
	children: ReactNode
	className?: string
}
const Modal: FC<Props> = ({ isOpen, isElevated = false, width, onSubmit, onClose, ariaLabel, children, className }) => {
	const [state, setState] = useState<State>(isOpen ? "open" : "idle")
	// Held in state rather than a ref because Portal mounts its container a render late, and the focus effect must
	// run again once the frame exists.
	const [frame, setFrame] = useState<HTMLElement | null>(null)
	const fallbackTitleId = useId()
	const [titleId, setTitleId] = useState<string>()
	const onCloseRef = useRef(onClose)
	onCloseRef.current = onClose

	useLayoutEffect(() => {
		if (!isOpen && state === "idle") return
		setState(isOpen ? "open" : "closed")
	}, [isOpen, state])

	useEffect(() => {
		if (state !== "closed") return

		const timeout = setTimeout(() => {
			setState("idle")
		}, 300)

		return () => clearTimeout(timeout)
	}, [state])

	useEffect(() => {
		if (state === "idle") return
		return lockBodyScroll()
	}, [state])

	// Name the dialog after its title. Runs after every render because the title can appear once content has loaded.
	useLayoutEffect(() => {
		const title = frame?.querySelector<HTMLElement>("h1, h2, h3")
		if (title === null || title === undefined) {
			setTitleId(undefined)
			return
		}
		if (title.id === "") title.id = fallbackTitleId
		setTitleId(title.id)
	})

	// Move focus into the dialog when it opens, keep it there, and give it back to the opener when it closes. A layout
	// effect, so focus returns in the same commit that makes the closing frame inert.
	useLayoutEffect(() => {
		if (!isOpen || frame === null) return

		const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
		if (!frame.contains(document.activeElement)) {
			const autoFocus = frame.querySelector<HTMLElement>("[autofocus], [data-autofocus]")
			;(autoFocus ?? getFocusable([frame], 1)[0] ?? frame).focus({ preventScroll: true })
		}

		openFrames.push(frame)

		const handleKeyDown = (event: KeyboardEvent) => {
			// Only the topmost modal reacts, so a modal opened from another one doesn't fight it for focus.
			// Input methods use Escape to cancel composition, which shouldn't close the modal.
			if (openFrames.at(-1) !== frame || event.defaultPrevented || event.isComposing) return

			if (event.key === "Escape") {
				if (onCloseRef.current === undefined) return
				event.preventDefault()
				onCloseRef.current()
				return
			}
			if (event.key !== "Tab") return

			// Focus moves through the scope explicitly, since in document order unrelated body content can sit between
			// the frame and a portal opened from it.
			event.preventDefault()
			moveFocus(frame, event.shiftKey ? -1 : 1)
		}
		document.addEventListener("keydown", handleKeyDown)

		// Focus can still arrive outside the scope without a Tab reaching this listener, such as when it leaves an
		// iframe, whose key events stay in the iframe. Bring it back into the modal.
		let lastFocused: Element | null = document.activeElement
		const handleFocusIn = (event: FocusEvent) => {
			if (openFrames.at(-1) !== frame || !(event.target instanceof HTMLElement)) return
			const scope = getFocusScope(frame)
			if (scope.some(element => element.contains(event.target as Node))) {
				lastFocused = event.target
				return
			}
			// Focus taken by a click outside, such as on a toast, goes back where it was.
			if (
				lastFocused instanceof HTMLElement &&
				!(lastFocused instanceof HTMLIFrameElement) &&
				lastFocused.isConnected &&
				frame.contains(lastFocused)
			) {
				lastFocused.focus({ preventScroll: true })
				if (document.activeElement === lastFocused) return
			}
			// Leaving an iframe that comes first in the modal backwards wraps to the end; anything else goes to the start.
			const focusable = getFocusable(scope, 1)
			const wasFirst = lastFocused !== null && lastFocused === focusable[0]
			moveFocus(frame, wasFirst ? -1 : 1, wasFirst ? focusable.indexOf(lastFocused as HTMLElement) : -1)
		}
		document.addEventListener("focusin", handleFocusIn)
		// Clicking into an iframe fires no focusin here; the window blurs with the iframe active instead.
		const handleWindowBlur = () => {
			if (document.activeElement instanceof HTMLIFrameElement) lastFocused = document.activeElement
		}
		window.addEventListener("blur", handleWindowBlur)

		return () => {
			document.removeEventListener("keydown", handleKeyDown)
			document.removeEventListener("focusin", handleFocusIn)
			window.removeEventListener("blur", handleWindowBlur)
			openFrames.splice(openFrames.indexOf(frame), 1)
			// Leave focus alone if another modal is still open and the opener is behind it.
			const topFrame = openFrames.at(-1)
			const canRestore = topFrame === undefined || (opener !== null && topFrame.contains(opener))
			const active = document.activeElement
			if (
				canRestore &&
				opener?.isConnected &&
				(active === null || active === document.body || frame.contains(active))
			)
				opener.focus({ preventScroll: true })
		}
	}, [isOpen, frame])

	if (state === "idle") return null

	const dialogProps = {
		ref: setFrame,
		// While closing, the frame is inert and leaves the accessibility tree instead of remaining a modal dialog.
		role: isOpen ? "dialog" : undefined,
		"aria-modal": isOpen ? true : undefined,
		"aria-labelledby": isOpen ? titleId : undefined,
		"aria-label": isOpen && titleId === undefined ? ariaLabel : undefined,
		inert: !isOpen,
		tabIndex: -1,
		$isOpen: isOpen,
		$width: width,
		className,
	} as const

	return (
		<Portal>
			<Container $isOpen={isOpen} $isElevated={isElevated}>
				{onSubmit ? (
					<Frame as={Form} {...dialogProps} onSubmit={onSubmit}>
						{children}
					</Frame>
				) : (
					<Frame {...dialogProps}>{children}</Frame>
				)}
			</Container>
		</Portal>
	)
}

const openFrames: HTMLElement[] = []

/**
 * The frame plus portals mounted after it, such as dropdown menus and pickers opened from inside the modal, so the
 * focus trap doesn't make them unreachable. Only the app's own portals count, not third-party widgets on the body.
 * Later modals are excluded by the topmost check.
 */
function getFocusScope(frame: HTMLElement) {
	let root: HTMLElement = frame
	while (root.parentElement !== null && root.parentElement !== document.body) root = root.parentElement
	const scope = [frame]
	for (let sibling = root.nextElementSibling; sibling !== null; sibling = sibling.nextElementSibling)
		if (sibling instanceof HTMLElement && sibling.dataset.portal !== undefined) scope.push(sibling)
	return scope
}

const focusableSelector = [
	"a[href]",
	"area[href]",
	"summary",
	"iframe",
	"audio[controls]",
	"video[controls]",
	// :enabled, unlike :not([disabled]), also excludes controls inside a disabled fieldset.
	"button:enabled",
	"input:enabled:not([type='hidden'])",
	"select:enabled",
	"textarea:enabled",
	"[contenteditable='true']",
	"[tabindex]",
].join(",")

/** Focusable elements in the scope, in order, with each radio group reduced to its single Tab stop. */
function getFocusable(scope: HTMLElement[], direction: 1 | -1) {
	const elements = scope
		.flatMap(element => [...element.querySelectorAll<HTMLElement>(focusableSelector)])
		.filter(
			element =>
				element.tabIndex >= 0 &&
				element.getClientRects().length > 0 &&
				getComputedStyle(element).visibility !== "hidden" &&
				!element.closest("[inert], [aria-hidden='true']"),
		)

	const radioGroups = new Map<string, HTMLInputElement[]>()
	for (const element of elements) {
		if (!(element instanceof HTMLInputElement) || element.type !== "radio" || element.name === "") continue
		const key = `${element.form?.id ?? ""}\u0000${element.name}`
		radioGroups.set(key, [...(radioGroups.get(key) ?? []), element])
	}
	const radioStops = new Set(
		[...radioGroups.values()].map(
			group => group.find(radio => radio.checked) ?? (direction === 1 ? group[0] : group.at(-1)),
		),
	)
	return elements.filter(
		element =>
			!(element instanceof HTMLInputElement && element.type === "radio" && element.name !== "") ||
			radioStops.has(element),
	)
}

/**
 * Moves focus to the next element in the modal's scope, wrapping at the ends. Skips candidates that don't actually
 * take focus, so one can't trap the keyboard, and falls back to the frame.
 */
function moveFocus(frame: HTMLElement, direction: 1 | -1, fromIndex?: number) {
	const focusable = getFocusable(getFocusScope(frame), direction)
	const currentIndex = fromIndex ?? focusable.indexOf(document.activeElement as HTMLElement)
	for (let step = 0; step < focusable.length; step++) {
		const index =
			currentIndex === -1
				? direction === 1
					? step
					: focusable.length - 1 - step
				: (((currentIndex + direction * (step + 1)) % focusable.length) + focusable.length) % focusable.length
		const candidate = focusable[index]!
		candidate.focus()
		if (document.activeElement === candidate) return
	}
	frame.focus()
}

const Container = styled.div<{ $isOpen: boolean; $isElevated: boolean }>`
	position: fixed;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
	${props =>
		props.$isElevated &&
		css`
			z-index: 100;
		`}
	background-color: ${props => (props.$isOpen ? "rgba(28, 28, 28, 0.6)" : "transparent")};
	backdrop-filter: ${props => (props.$isOpen ? "blur(8px)" : "none")};
	pointer-events: ${props => (props.$isOpen ? "all" : "none")};
	padding: 8px;
	display: flex;
	justify-content: center;
	align-items: center;
	transition: 0.15s background-color;
`

const openAnimation = keyframes`
	from {
		opacity: 0;
		transform: translateY(24px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
`

const closeAnimation = keyframes`
	from {
		opacity: 1;
		transform: translateY(0);
	}
	to {
		opacity: 0;
		transform: translateY(24px);
	}
`

const Frame = styled.div<{ $isOpen: boolean; $width?: string | number }>`
	${props =>
		props.$width !== undefined &&
		css`
			width: ${typeof props.$width === "number" ? `${props.$width}px` : props.$width};
			max-width: 100%;
		`}
	max-height: 95dvh;
	overflow-y: auto;
	background-color: white;
	display: flex;
	flex-direction: column;
	align-items: stretch;
	opacity: 0;
	transform: translateY(24px);
	animation: ${props => (props.$isOpen ? openAnimation : closeAnimation)} 0.3s forwards;
	transition: 0.3s width;

	&:focus {
		outline: none;
	}
`

export default Modal
